import React, { useState, useEffect } from 'react';
import { adminApi } from '../../../services/api';
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { UserX, ShieldCheck, DollarSign, AlertTriangle } from 'lucide-react';
import { ACCENT, Spinner } from './shared';

function RfmKpiCard({ label, value, sub, icon: Icon, color, testid }) {
  return (
    <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[#64748B] text-[11px] font-medium">{label}</p>
        {Icon && <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}><Icon size={14} style={{ color }} /></div>}
      </div>
      <p className="text-xl font-bold tabular-nums" style={{ color }}>{value}</p>
      {sub && <p className="text-[#64748B] text-[10px] mt-0.5">{sub}</p>}
    </div>
  );
}

export function RfmTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.get('/admin/analytics/rfm')
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;
  if (!data || !data.kpis) return <p className="text-[#64748B] text-sm text-center py-8">Nessun dato disponibile</p>;

  const { kpis, segments, distribution, scatter } = data;

  return (
    <div className="space-y-6" data-testid="rfm-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <RfmKpiCard label="Tasso di Churn" value={`${kpis.churn_rate}%`} sub={`${kpis.churned_count} clienti persi`}
          icon={UserX} color={kpis.churn_rate > 30 ? '#EF4444' : kpis.churn_rate > 15 ? '#F59E0B' : '#16A34A'} testid="rfm-churn-rate" />
        <RfmKpiCard label="Retention Rate" value={`${kpis.retention_rate}%`} sub={`${kpis.total_customers} clienti attivi`}
          icon={ShieldCheck} color={kpis.retention_rate > 70 ? '#16A34A' : kpis.retention_rate > 50 ? '#F59E0B' : '#EF4444'} testid="rfm-retention-rate" />
        <RfmKpiCard label="CLV Stimato" value={`\u20AC${kpis.clv_estimate}`} sub="valore annualizzato"
          icon={DollarSign} color="#3B82F6" testid="rfm-clv" />
        <RfmKpiCard label="A Rischio" value={kpis.at_risk_count} sub="richiedono attenzione"
          icon={AlertTriangle} color="#F59E0B" testid="rfm-at-risk" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-green-50 border border-green-100 rounded-2xl p-4">
          <p className="text-green-800 text-xs font-medium">Champions</p>
          <p className="text-green-700 text-2xl font-bold mt-1">{kpis.champions_count}</p>
          <p className="text-green-600 text-[10px] mt-0.5">I tuoi migliori clienti</p>
        </div>
        <div className="bg-white border border-slate-100 rounded-2xl p-4">
          <p className="text-[#64748B] text-xs font-medium">Spesa media</p>
          <p className="text-[#1A1A1A] text-2xl font-bold mt-1">{'\u20AC'}{kpis.avg_spend}</p>
          <p className="text-[#aaa] text-[10px] mt-0.5">per cliente</p>
        </div>
        <div className="bg-white border border-slate-100 rounded-2xl p-4">
          <p className="text-[#64748B] text-xs font-medium">Visite medie</p>
          <p className="text-[#1A1A1A] text-2xl font-bold mt-1">{kpis.avg_visits}</p>
          <p className="text-[#aaa] text-[10px] mt-0.5">per cliente</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
        <h3 className="font-semibold text-[#1A1A1A] text-sm mb-4">Distribuzione Segmenti RFM</h3>
        {distribution.length > 0 ? (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={distribution} layout="vertical" margin={{ left: 10, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#999' }} />
                <YAxis type="category" dataKey="segment" tick={{ fontSize: 11, fill: '#333' }} width={100} />
                <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #eee', fontSize: '12px' }}
                  formatter={(v, name, props) => [`${v} clienti (${props.payload.pct}%)`, 'Clienti']} />
                <Bar dataKey="count" radius={[0, 6, 6, 0]} maxBarSize={28}>
                  {distribution.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="text-[#64748B] text-sm text-center py-8">Dati insufficienti</p>
        )}
      </div>

      {scatter && scatter.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
          <h3 className="font-semibold text-[#1A1A1A] text-sm mb-1">Mappa RFM Clienti</h3>
          <p className="text-[#999] text-[10px] mb-4">Ogni punto rappresenta un cliente. Asse X = Frequenza, Asse Y = Recency. Dimensione = Spesa</p>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={(() => {
                const grid = {};
                scatter.forEach(s => {
                  const key = `R${s.r}-F${s.f}`;
                  if (!grid[key]) grid[key] = { rf: key, r: s.r, f: s.f, count: 0, segments: {} };
                  grid[key].count++;
                  grid[key].segments[s.segment] = (grid[key].segments[s.segment] || 0) + 1;
                });
                return Object.values(grid).sort((a, b) => a.f - b.f || a.r - b.r);
              })()} margin={{ left: 0, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="rf" tick={{ fontSize: 9, fill: '#999' }} />
                <YAxis tick={{ fontSize: 10, fill: '#999' }} />
                <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #eee', fontSize: '11px' }}
                  formatter={(v) => [`${v} clienti`]} />
                <Bar dataKey="count" fill={ACCENT} radius={[4, 4, 0, 0]} maxBarSize={20} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div>
        <h3 className="font-semibold text-[#1A1A1A] text-sm mb-3">Dettaglio Segmenti</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {Object.entries(segments).filter(([_, s]) => s.count > 0).sort((a, b) => b[1].count - a[1].count).map(([id, seg]) => (
            <div key={id} className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm" data-testid={`rfm-segment-${id}`}>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: seg.color }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm text-[#1A1A1A]">{seg.label}</span>
                    <span className="text-xs font-bold text-[#D44E20] tabular-nums">{seg.count}</span>
                    <span className="text-[10px] text-[#999]">({seg.pct}%)</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4 mb-2">
                <span className="text-[10px] text-[#64748B]">Media visite: <strong className="text-[#333]">{seg.avg_visits}</strong></span>
                <span className="text-[10px] text-[#64748B]">Spesa media: <strong className="text-[#333]">{'\u20AC'}{seg.avg_spend}</strong></span>
              </div>
              <div className="bg-gray-50 rounded-lg p-2.5">
                <p className="text-[10px] text-[#64748B] leading-relaxed">{seg.tip}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
